package com.evoapps.evoshift.ui.finance

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.PersonSearch
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.who_worked_hint
import evoshift.composeapp.generated.resources.who_worked_no_staff
import evoshift.composeapp.generated.resources.who_worked_nobody
import evoshift.composeapp.generated.resources.who_worked_pick
import evoshift.composeapp.generated.resources.who_worked_pick_description
import evoshift.composeapp.generated.resources.who_worked_shift
import evoshift.composeapp.generated.resources.who_worked_nobody_recorded
import evoshift.composeapp.generated.resources.work_hours_change
import evoshift.composeapp.generated.resources.who_worked_title
import org.jetbrains.compose.resources.stringResource

/** Test tag of one "Who worked this shift?" card, followed by the shift's id. */
const val WHO_WORKED_TAG = "who_worked_"
const val NOBODY_WORKED_TAG = "nobody_worked_"
const val NOBODY_WORKED_CHANGE_TAG = "nobody_worked_change_"

/**
 * SH-128: an open shift on a past day (or today) nobody was assigned to has no labour cost. On the
 * navy day dialog and pane, one card per such shift asks who worked it: a person from the
 * workplace's [staff] ([onRecord] with their id) or "Nobody worked" (null; it stops asking).
 * Marked in gold as needing attention.
 */
@Composable
internal fun WhoWorkedSection(
    shifts: List<Shift>,
    staff: List<User>,
    onRecord: (shiftId: String, workerId: String?) -> Unit,
    /** SH-262: open shifts already marked "Nobody worked", listed below to be changed. */
    nobodyWorked: List<Shift> = emptyList()
) {
    if (shifts.isEmpty() && nobodyWorked.isEmpty()) return
    var reopened by remember { mutableStateOf(emptySet<String>()) }
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        shifts.forEach { shift -> WhoWorkedCard(shift, staff, onRecord) }
        nobodyWorked.forEach { shift ->
            if (shift.id in reopened) WhoWorkedCard(shift, staff) { id, worker -> reopened = reopened - id; onRecord(id, worker) }
            else NobodyWorkedRow(shift) { reopened = reopened + shift.id }
        }
    }
}

/** SH-262: "Nobody worked · 06:30–14:00" with Change, which asks who worked it again. */
@Composable
private fun NobodyWorkedRow(shift: Shift, onChange: () -> Unit) {
    val shape = RoundedCornerShape(14.dp)
    Row(
        Modifier
            .fillMaxWidth()
            .testTag(NOBODY_WORKED_TAG + shift.id)
            .background(Color.White.copy(alpha = 0.04f), shape)
            .border(1.dp, Color.White.copy(alpha = 0.15f), shape)
            .padding(start = 12.dp, end = 4.dp, top = 4.dp, bottom = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            stringResource(Res.string.who_worked_nobody_recorded, shiftTimes(shift.plannedTime.start, shift.plannedTime.end)),
            style = MaterialTheme.typography.bodyMedium, color = Color.White.copy(alpha = 0.8f), modifier = Modifier.weight(1f)
        )
        TextButton(
            onClick = onChange,
            colors = ButtonDefaults.textButtonColors(contentColor = LightGold),
            modifier = Modifier.handCursor().testTag(NOBODY_WORKED_CHANGE_TAG + shift.id)
        ) {
            Text(stringResource(Res.string.work_hours_change))
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun WhoWorkedCard(shift: Shift, staff: List<User>, onRecord: (shiftId: String, workerId: String?) -> Unit) {
    val times = shiftTimes(shift.plannedTime.start, shift.plannedTime.end)
    val shape = RoundedCornerShape(14.dp)
    Column(
        Modifier
            .fillMaxWidth()
            .testTag(WHO_WORKED_TAG + shift.id)
            .background(LightGold.copy(alpha = 0.12f), shape)
            .border(1.dp, LightGold.copy(alpha = 0.8f), shape)
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.PersonSearch, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(8.dp))
            Text(stringResource(Res.string.who_worked_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = LightGold)
        }
        Text(stringResource(Res.string.who_worked_shift, times), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold, color = Color.White)
        Text(stringResource(Res.string.who_worked_hint), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.75f))
        if (staff.isEmpty()) {
            Text(stringResource(Res.string.who_worked_no_staff), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.75f))
        }
        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            if (staff.isNotEmpty()) PersonPicker(times, staff, onPick = { onRecord(shift.id, it) })
            TextButton(
                onClick = { onRecord(shift.id, null) },
                colors = ButtonDefaults.textButtonColors(contentColor = Color.White),
                modifier = Modifier.handCursor()
            ) {
                Text(stringResource(Res.string.who_worked_nobody))
            }
        }
    }
}

/** "Choose a person": a menu of the workplace's staff. */
@Composable
private fun PersonPicker(times: String, staff: List<User>, onPick: (String) -> Unit) {
    var open by remember { mutableStateOf(false) }
    val description = stringResource(Res.string.who_worked_pick_description, times)
    Box {
        OutlinedButton(
            onClick = { open = true },
            colors = ButtonDefaults.outlinedButtonColors(contentColor = LightGold),
            border = BorderStroke(1.dp, LightGold.copy(alpha = 0.7f)),
            modifier = Modifier.handCursor().semantics { contentDescription = description }
        ) {
            Text(stringResource(Res.string.who_worked_pick))
            Icon(Icons.Default.ArrowDropDown, contentDescription = null, modifier = Modifier.size(18.dp))
        }
        DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
            staff.forEach { person ->
                DropdownMenuItem(
                    text = { Text(person.displayName) },
                    onClick = {
                        open = false
                        onPick(person.uid)
                    },
                    modifier = Modifier.handCursor()
                )
            }
        }
    }
}

/** "07:00–15:00" from a shift's ISO times. */
internal fun shiftTimes(start: String, end: String): String =
    "${start.substringAfter('T').take(5)}–${end.substringAfter('T').take(5).ifEmpty { "…" }}"
